import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import FarmMap from '../components/FarmMap';
import YouTubeEmbed from '../components/YouTubeEmbed';
import SofixOverlayStory from '../components/SofixOverlayStory';
import {
  getFarmerBySlug,
  listFarmerMonthlyReports,
  listPublicPostsByFarmerTag,
  listFarmerSoilTests,
} from '../lib/firestore';
import { useAuth } from '../hooks/useAuth';
import ExpandableText from '../components/ExpandableText';
import { Link } from 'react-router-dom';
import {
  getFeaturedFarmerBySlug,
  mergeFeaturedFarmers,
  mergeFeaturedFarmerSoilTests,
} from '../data/farmers';
import { sanitizeArticleHtml } from '../lib/articleHtml';
import Seo from '../components/Seo';
import { toAbsoluteUrl, truncateText } from '../lib/seo';

const tabs = [
  { key: 'philosophy', label: '栽培の哲学' },
  { key: 'soil', label: '土づくり' },
  { key: 'evidence', label: '検査結果' },
];

export default function FarmerDetailPage() {
  const { slug } = useParams();
  const { hash } = useLocation();
  const { isAdmin, userProfile } = useAuth();
  const [farmer, setFarmer] = useState(null);
  const [soilTests, setSoilTests] = useState([]);
  const [reports, setReports] = useState([]);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('philosophy');

  useEffect(() => {
    async function run() {
      const found = await getFarmerBySlug(slug);
      const mergedFarmer = found ? mergeFeaturedFarmers([found]).find((row) => row.slug === slug || row.id === found.id) : getFeaturedFarmerBySlug(slug);
      const resolved = mergedFarmer || found;
      setFarmer(resolved);
      if (!resolved?.id) return;
      const [tests, monthly] = await Promise.all([
        listFarmerSoilTests(resolved.id),
        listFarmerMonthlyReports(resolved.id),
      ]);
      const taggedPosts = await listPublicPostsByFarmerTag(resolved.id);
      setSoilTests(mergeFeaturedFarmerSoilTests([resolved.slug, resolved.id, slug], tests));
      setReports(monthly);
      setRelatedPosts(taggedPosts);
    }
    run();
  }, [slug]);

  useEffect(() => {
    if (!hash || !hash.startsWith('#monthly-report-')) return;

    const targetId = hash.slice(1);
    let attempts = 0;
    const maxAttempts = 12;

    const scrollToReport = () => {
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'auto', block: 'start' });
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        window.setTimeout(scrollToReport, 120);
      }
    };

    scrollToReport();
  }, [hash, reports.length]);

  const latestTest = soilTests[soilTests.length - 1];
  const latestReport = reports[0];
  const visibleRelatedPosts = relatedPosts.slice(0, 3);

  const metricsSummary = useMemo(() => {
    if (!latestTest?.metrics) return [];
    return ['pH', 'EC', 'N', 'P', 'K']
      .map((key) => ({ key, value: latestTest.metrics?.[key] }))
      .filter((row) => row.value !== undefined && row.value !== null);
  }, [latestTest]);

  if (!farmer) return <p>農家データを読み込み中...</p>;

  const imageUrl = farmer.heroImageUrl || farmer.profileImageUrl;
  const locationText = farmer.fieldAddress || [farmer.prefecture, farmer.city].filter(Boolean).join(' ');
  const contactUrl = farmer.contactUrl || farmer.instagramUrl || (farmer.instagramHandle ? `https://www.instagram.com/${farmer.instagramHandle}/` : '');
  const contactLabel = farmer.contactLabel || (contactUrl ? 'Instagram' : '');
  const contactText = farmer.contactText || contactLabel;
  const farmerTags = [
    farmer.farmerType === 'fulltime' ? '専業農家' : null,
    farmer.farmerType === 'parttime' ? 'ライフスタイル農家' : null,
    ...(farmer.crops || []),
  ].filter(Boolean);
  const canEditFarmerReports = Boolean(
    isAdmin
    || (
      userProfile?.role === 'farmer'
      && (
        userProfile?.linkedFarmerId === farmer.id
        || userProfile?.linkedFarmerSlug === farmer.slug
      )
    )
  );

  return (
    <>
      <Seo
        title={farmer.name}
        description={truncateText(farmer.description || farmer.story || `${farmer.name} の栽培背景や土づくりを紹介します。`)}
        path={`/farmers/${farmer.slug || farmer.id}`}
        image={imageUrl || toAbsoluteUrl('/favicon.png')}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ProfilePage',
          mainEntity: {
            '@type': 'Person',
            name: farmer.ownerName || farmer.name,
            description: truncateText(farmer.description || farmer.story || ''),
          },
        }}
      />
      <article className="detail-layout">
        <section className="panel">
          <Link to="/farmers" className="detail-back-link">← 農家一覧へ戻る</Link>
          <h1>{farmer.name}</h1>
          {farmer.ownerName ? <p className="meta-text">{farmer.ownerName}</p> : null}
          {farmerTags.length ? (
            <div className="tag-row" aria-label="農家タグ">
              {farmerTags.map((tag) => (
                <span key={`${farmer.id}-${tag}`} className="pill">{tag}</span>
              ))}
            </div>
          ) : null}
          {farmer.youtubeUrl ? (
            <>
              <YouTubeEmbed url={farmer.youtubeUrl} title={`${farmer.name} の紹介動画`} />
              <p className="meta-text">
                <a href={farmer.youtubeUrl} target="_blank" rel="noreferrer">YouTubeで開く</a>
              </p>
            </>
          ) : imageUrl ? (
            <img className="doctor-detail-media" src={imageUrl} alt={farmer.name} loading="lazy" />
          ) : null}
          {contactUrl ? (
            <div className="detail-info-grid">
              <div className="detail-info-card">
                <p className="meta-text">連絡先</p>
                <p><a href={contactUrl} target="_blank" rel="noreferrer">{contactText}</a></p>
              </div>
              {farmer.websiteUrl && farmer.websiteUrl !== contactUrl ? (
                <div className="detail-info-card">
                  <p className="meta-text">Webサイト</p>
                  <p><a href={farmer.websiteUrl} target="_blank" rel="noreferrer">{farmer.websiteUrl}</a></p>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="tab-row" role="tablist" aria-label="農家情報タブ">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`tab-btn ${activeTab === tab.key ? 'is-active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
                type="button"
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'philosophy' && (
            <div className="tab-panel" role="tabpanel">
              <h2>栽培の哲学</h2>
              <ExpandableText>
                {farmer.philosophyText || '土地の特性を読み取り、無理なく循環する栽培を重視しています。生産背景を丁寧に伝え、食べる側との距離を縮めることを目指しています。'}
              </ExpandableText>
            </div>
          )}

          {activeTab === 'soil' && (
            <div className="tab-panel" role="tabpanel">
              <h2>土づくり</h2>
              <ExpandableText>
                {farmer.soilBuildingText || '土壌分析に基づいて圃場を観察し、栽培計画を調整しています。化学的な数値と現場感覚を合わせ、継続的に見直しを行います。'}
              </ExpandableText>
            </div>
          )}

          {activeTab === 'evidence' && (
            <div className="tab-panel" role="tabpanel">
              <h2>検査結果</h2>
              <p>検査日: {latestTest?.date || '未登録'} / 採取地点: {latestTest?.sampleLocation || '未登録'} / 検査機関: {latestTest?.labName || '未登録'}</p>
              {latestTest?.overlayGuide ? (
                <SofixOverlayStory guide={latestTest.overlayGuide} />
              ) : null}
              <div className="info-callout">
                <p className="info-callout-title">SOFIXとは？</p>
                <p>専門的な土壌分析を、一般の方にも分かる形で読み解くための案内ページです。</p>
                <Link className="text-btn" to="/sofix">SOFIXの説明を見る</Link>
              </div>
            </div>
          )}
        </section>

        <section className="panel" id="visibility">
          <h2>生産の見える化</h2>
          <div className="trace-overview-grid">
            <article className="trace-card trace-card-location">
              <h3>場所</h3>
              <FarmMap
                geo={farmer.geo}
                label={farmer.name}
                address={locationText}
                mapUrl={farmer.mapUrl}
                camera={farmer.mapCamera}
              />
            </article>
            <article className="trace-card">
              <h3>土壌指標</h3>
              <div className="metric-grid">
                {metricsSummary.map((metric) => (
                  <div className="metric-card" key={metric.key}>
                    <p className="metric-key">{metric.key}</p>
                    <p className="metric-value">{metric.value}</p>
                  </div>
                ))}
              </div>
              <p className="meta-text">検査日: {latestTest?.date || '未登録'} / 検査機関: {latestTest?.labName || '未登録'}</p>
            </article>
            <article className="trace-card">
              <h3>関連する記事</h3>
              {visibleRelatedPosts.length ? (
                <div className="article-preview-list">
                  {visibleRelatedPosts.map((post) => (
                    <article key={post.id} className="article-preview-item">
                      <strong>{post.title}</strong>
                      {post.tags?.length ? <p className="meta-text">タグ: {post.tags.join(' / ')}</p> : null}
                      {post.cropTags?.length ? <p className="meta-text">野菜タグ: {post.cropTags.join(' / ')}</p> : null}
                      <Link className="text-btn" to={`/posts/${post.slug}`}>記事を読む</Link>
                    </article>
                  ))}
                </div>
              ) : (
                <p>関連する記事はまだありません。</p>
              )}
            </article>
            <article className="trace-card">
              <h3>検査原本</h3>
              <p>要約を確認した上で、原本PDFをご確認いただけます。</p>
              {(latestTest?.pdfEmbedUrl || latestTest?.pdfUrl) ? (
                <a className="btn btn-secondary" href={latestTest.pdfEmbedUrl || latestTest.pdfUrl} target="_blank" rel="noreferrer">原本を見る</a>
              ) : <p>原本PDFリンクは未登録です。</p>}
              <div className="info-callout info-callout-compact">
                <p className="info-callout-title">SOFIXとは？</p>
                <Link className="text-btn" to="/sofix">SOFIXの説明を見る</Link>
              </div>
            </article>
          </div>
        </section>

        <section className="panel">
          <h2>月次レポート</h2>
          <ul className="timeline">
            {reports.map((report) => (
              <li key={report.id} id={`monthly-report-${report.id}`}>
                <strong>{report.title || report.highlights || report.publishedDate || report.month}</strong>
                <p>{report.publishedDate || report.month}</p>
                {report.contentHtml ? (
                  <div className="article article-rich-content" dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(report.contentHtml) }} />
                ) : (
                  <p>{report.body}</p>
                )}
                {(report.tags?.length || report.cropTags?.length) ? <p className="meta-text">タグ: {(report.tags || report.cropTags).join(' / ')}</p> : null}
                {canEditFarmerReports ? (
                  <p>
                    <Link
                      className="btn btn-secondary"
                      to={`/admin/reports?editor=farmer&mode=edit&farmerId=${encodeURIComponent(farmer.id)}&reportId=${encodeURIComponent(report.id)}`}
                    >
                      編集
                    </Link>
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      </article>
    </>
  );
}
